import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../api/api";
import { setLoading } from "../../loading/loadingSlice";

// GET ALL NURSES
export const getAllNurse = createAsyncThunk("getAllNurse", async ({ page, limit, search, status, silent = false, ...paramsArg }, { dispatch, rejectWithValue }) => {
  try {
    if (!silent) dispatch(setLoading(true));
    const params = {
      page,
      limit,
      search,
      status,
      sort: "-updatedAt",
      searchFields: "employeeID,email,phoneNumber",
      ...paramsArg,
    };

    const response = await api.get(`/api/v1/cc/nursing_care/nurse`, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching nurse list:", error);
    return rejectWithValue(error.response?.data?.message || "Error fetching nurse list");
  } finally {
    if (!silent) dispatch(setLoading(false));
  }
});

export const getNurseDetails = createAsyncThunk("getNurseDetails", async (nurseId, { dispatch }) => {
  try {
    dispatch(setLoading(true));
    const response = await api.get(`/api/v1/cc/nursing_care/nurse/${nurseId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching nurse details:", error);
    const errorMessage = error.response && error.response.data ? error.response.data.message : "Error fetching nurse details";
    throw new Error(errorMessage);
  } finally {
    dispatch(setLoading(false));
  }
});

// POST A NEW NURSE
export const postNurse = createAsyncThunk("postNurse", async (nurseData, { dispatch, rejectWithValue }) => {
  try {
    dispatch(setLoading(true));
    const response = await api.post(`/api/v1/cc/nursing_care/nurse`, nurseData);
    return response.data;
  } catch (error) {
    console.error("Error posting nurse data:", error);
    return rejectWithValue(error.response?.data?.message || "Error posting nurse data");
  } finally {
    dispatch(setLoading(false));
  }
});

// Edit Nurse
export const editNurse = createAsyncThunk("editNurse", async ({ nurseId, nurseData }, { dispatch, rejectWithValue }) => {
  try {
    dispatch(setLoading(true));
    const response = await api.patch(`/api/v1/cc/nursing_care/nurse/${nurseId}`, nurseData);
    return response.data;
  } catch (error) {
    console.error("Error editing nurse data:", error);
    return rejectWithValue(error.response?.data?.message || "Error editing nurse data");
  } finally {
    dispatch(setLoading(false));
  }
});

// GET ALL SERVICES
export const getAllServices = createAsyncThunk("getAllServices", async (params = {}, { dispatch, rejectWithValue }) => {
  const { page = 1, limit = 100, isActive = true } = params;

  try {
    dispatch(setLoading(true));

    console.log("Thunk executing...");
    console.log("Params:", page, limit);

    const response = await api.get(`/api/v1/cc/nursing_care/service`, { params: { page, limit, isActive } });

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message);
  } finally {
    dispatch(setLoading(false));
  }
});

// GET SUB-SERVICES BY SERVICE ID
export const getSubServices = createAsyncThunk("getSubServices", async (paramsArg = {}, { dispatch, rejectWithValue }) => {
  try {
    dispatch(setLoading(true));
    const params = typeof paramsArg === "string" ? { service: paramsArg, limit: 20 } : { limit: 20, ...paramsArg };

    const response = await api.get(`/api/v1/cc/nursing_care/service/sub_service`, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching sub-services:", error);
    return rejectWithValue(error.response?.data?.message || "Error fetching sub-services");
  } finally {
    dispatch(setLoading(false));
  }
});

// GET SERVICE ITEMS BY SERVICE ID & SUB-SERVICE ID
export const getServiceItems = createAsyncThunk("getServiceItems", async (params = {}, { dispatch, rejectWithValue }) => {
  const { page = 1, limit = 10 } = params;

  try {
    dispatch(setLoading(true));

    const response = await api.get(`/api/v1/cc/nursing_care/service/service_item`, {
      params: { page, limit },
    });

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Error fetching service items");
  } finally {
    dispatch(setLoading(false));
  }
});

export const deleteNurse = createAsyncThunk("deleteNurse", async (nurseId, { dispatch, rejectWithValue }) => {
  try {
    dispatch(setLoading(true));
    await api.delete(`/api/v1/cc/nursing_care/nurse/${nurseId}`);
    return nurseId;
  } catch (error) {
    console.error("Error deleting nurse:", error);
    return rejectWithValue(error.response?.data?.message || "Error deleting nurse");
  } finally {
    dispatch(setLoading(false));
  }
});

export const createService = createAsyncThunk("createService", async ({ title }, { dispatch, rejectWithValue }) => {
  try {
    dispatch(setLoading(true));

    const response = await api.post(`/api/v1/cc/nursing_care/service`, {
      title,
      isActive: true,
    });

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Error creating service");
  } finally {
    dispatch(setLoading(false));
  }
});

export const createSubService = createAsyncThunk("createSubService", async ({ title, serviceId }, { dispatch, rejectWithValue }) => {
  try {
    dispatch(setLoading(true));

    const response = await api.post(`/api/v1/cc/nursing_care/service/sub_service`, {
      title,
      service: serviceId,
    });

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Error creating sub service");
  } finally {
    dispatch(setLoading(false));
  }
});

export const toggleNurseActiveStatus = createAsyncThunk("manageNurse/toggleNurseActiveStatus", async ({ nurseId /*, startDate = null, endDate = null*/ }, { dispatch, rejectWithValue }) => {
  try {
    dispatch(setLoading(true));

    const body = {};

    /*if (startDate && endDate) {
      body.startDate = startDate;
      body.endDate = endDate;
    }*/

    const response = await api.patch(`/api/v1/cc/nursing_care/nurse/toggle-status/${nurseId}`, body);

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Error toggling nurse status");
  } finally {
    dispatch(setLoading(false));
  }
});

export const bulkUploadNurse = createAsyncThunk("bulkUploadNurse", async (file, { dispatch, rejectWithValue }) => {
  try {
    dispatch(setLoading(true));
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(`/api/v1/cc/nursing_care/nurse/bulk-upload`, formData);
    return response.data;
  } catch (error) {
    console.error("Error bulk uploading nurses:", error);
    return rejectWithValue(error.response?.data?.message || "Error bulk uploading nurses");
  } finally {
    dispatch(setLoading(false));
  }
});
